// src/data/resources/index.ts

import React from "react";
import { categories } from "./categories";
import { articles } from "./articlesIndex";

// Types
export interface Article {
  id: string;
  categoryId: string;
  title: string;
  content: string;
  excerpt?: string;
  featuredImage?: string;
  order?: number;
  featured?: boolean;
}

export interface Category {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  featuredImage?: string;
  articles: Article[];
}

// Helper function to get all categories with their articles
export function getAllCategories(): Category[] {
  return categories.map(category => {
    const categoryArticles = articles.filter(article => article.categoryId === category.id);
    
    // Sort articles by order if available
    categoryArticles.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return 0;
    });
    
    return {
      ...category,
      articles: categoryArticles
    };
  });
}

// Get a specific category and its articles
export function getCategoryData(categoryId: string): Category | undefined {
  const category = categories.find(c => c.id === categoryId);
  if (!category) return undefined;
  
  const categoryArticles = articles.filter(article => article.categoryId === categoryId);
  
  // Sort articles by order if available
  categoryArticles.sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    return 0;
  });
  
  return {
    ...category,
    articles: categoryArticles
  };
}

// Get featured articles
export function getFeaturedArticles(limit: number = 3): Array<Article & { categoryName: string }> {
  const featuredArticles = articles
    .filter(article => article.featured)
    .slice(0, limit);
    
  return featuredArticles.map(article => {
    const category = categories.find(c => c.id === article.categoryId);
    return {
      ...article,
      categoryName: category?.title || ""
    };
  });
}

// Search functionality
export function searchResources(query: string): Array<Article & { categoryName: string }> {
  if (!query || query.trim() === '') {
    return [];
  }
  
  const searchQuery = query.toLowerCase().trim();
  
  const searchResults = articles.filter(article => {
    const searchText = `${article.title} ${article.content} ${article.excerpt || ""}`.toLowerCase();
    return searchText.includes(searchQuery);
  });
  
  return searchResults.map(article => {
    const category = categories.find(c => c.id === article.categoryId);
    return {
      ...article,
      categoryName: category?.title || ""
    };
  });
}

export function parseArticleHeaders(content: string): { id: string, title: string, level: number }[] {
  const headings: { id: string, title: string, level: number }[] = [];
  if (!content) return headings;
  
  // Use regex to find all h3, h4, h5, h6 tags and their IDs
  const regex = /<h([3-6])[^>]*id=["']([^"']+)["'][^>]*>([^<]+)<\/h\1>/g;
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    const level = parseInt(match[1], 10);
    const id = match[2];
    const title = match[3].trim();
    
    headings.push({ id, title, level });
  }
  
  return headings;
}